// vrm_core.wasm glue (APP_REARCH §3e): ONE module for the browser and the
// game server. CommonJS so the game server require()s it without Node's
// require(esm) warning; vrm_core.mjs re-exports it for ESM importers. The
// committed build is vrm_core/dist/vrm_core.wasm (== a fresh cargo build,
// scripts/check-server-ground.mjs; no GROUND-M build ABI: that is
// vrm_core_build.wasm, the engine's, engine/steps/_gm/wasm.mjs). The wasm is a raw C ABI (vrm_core/wasm/src/lib.rs): handles are pointers,
// arrays move through vc_alloc'ed memory. A view is re-made after every call
// that may allocate (a grown memory detaches the old buffer).
//
//   const core = instantiateSync(fs.readFileSync('dist/vrm_core.wasm')); // node
//   const core = await instantiate(fetch(url));                          // browser
//   const g = core.ground(); g.add(nodeBytes); g.skinY(x, z);            // GroundTree.skinY
//   core.hf(u16, W, H, cell, zBase, zScale).height(x, z);                // Heightfield.height
//   core.spans(spnBytes).resolve(x, z, feetY, surfaceY);                 // Spans.resolve
'use strict';

class Core {
  constructor(instance) {
    this.e = instance.exports;
  }

  _u8(p, n) { return new Uint8Array(this.e.memory.buffer, p, n); }
  _f64(p, n) { return new Float64Array(this.e.memory.buffer, p, n); }

  // copy a typed array in (-> ptr); the caller frees with _free(p, bytes)
  _in(a) {
    const b = new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
    const p = this.e.vc_alloc(b.length);
    this._u8(p, b.length).set(b);
    return p;
  }
  _free(p, n) { this.e.vc_free(p, n); }
  _err() { return new TextDecoder().decode(this._u8(this.e.vc_err_ptr(), this.e.vc_err_len())); }

  // [[name, version]] per kernel (vrm_core KERNELS)
  kernels() {
    const e = this.e, out = [];
    for (let i = 0; i < e.vc_kernel_count(); i++) {
      out.push([new TextDecoder().decode(this._u8(e.vc_kernel_name_ptr(i), e.vc_kernel_name_len(i))), e.vc_kernel_version(i)]);
    }
    return out;
  }

  // one GM03 node's bytes -> its GMIX1 index (a copy; Ground.addIndex takes
  // it on any instance of the same artefact); throws on bytes decodeNode refuses
  indexNode(bytes) {
    const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const p = this._in(b), lp = this.e.vc_alloc(4), r = this.e.vc_gm_index(p, b.length, lp);
    this._free(p, b.length);
    const n = new Uint32Array(this.e.memory.buffer, lp, 1)[0];
    this._free(lp, 4);
    if (!r) throw new Error(this._err());
    const out = this._u8(r, n).slice();
    this._free(r, n);
    return out;
  }

  hf(u16, w, h, cell, zBase, zScale) { return new Hf(this, u16, w, h, cell, zBase, zScale); }
  ground() { return new Ground(this); }
  // null for a truncated or foreign file (loadSpans)
  spans(bytes) {
    const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const p = this._in(b), h = this.e.vc_spans_new(p, b.length);
    this._free(p, b.length);
    return h ? new Spans(this, h) : null;
  }

  // lodmath
  triSplit(h00, h10, h01, h11, dx, dz) { return this.e.vc_tri_split(h00, h10, h01, h11, dx, dz); }
  levelStride(patchVerts) { return this.e.vc_level_stride(patchVerts); }
  strideSrc(ii, stride) { return this.e.vc_stride_src(ii, stride); }
  ringRadius(r, extentX, extentZ) { return this.e.vc_ring_radius(r.frame ? 1 : 0, r.radius_m, extentX, extentZ); }
  _specs(specs) {
    const a = new Float64Array(5 * specs.length);
    specs.forEach((s, i) => a.set([s.tile_m, s.patch_verts, s.px_per_vertex, s.range_min_m, s.range_max_m], 5 * i));
    return a;
  }
  focusLevelRange(spec, fovDeg, rtPx) {
    const a = this._specs([spec]), p = this._in(a), r = this.e.vc_focus_level_range(p, fovDeg, rtPx);
    this._free(p, a.byteLength);
    return r;
  }
  // {level: range_m}, as lodmath.focusRanges
  focusRanges(specs, fovDeg, rtPx) {
    const a = this._specs(specs), p = this._in(a), o = this.e.vc_alloc(8 * specs.length);
    this.e.vc_focus_ranges(p, specs.length, fovDeg, rtPx, o);
    const v = this._f64(o, specs.length), out = {};
    specs.forEach((s, i) => { out[s.level] = v[i]; });
    this._free(p, a.byteLength); this._free(o, 8 * specs.length);
    return out;
  }
  focusSweepFovs(specs, rtPx, fovLo, fovHi) {
    const a = this._specs(specs), p = this._in(a), cap = 8 * (specs.length + 2), o = this.e.vc_alloc(cap);
    const n = this.e.vc_focus_sweep_fovs(p, specs.length, rtPx, fovLo, fovHi, o);
    const out = Array.from(this._f64(o, n));
    this._free(p, a.byteLength); this._free(o, cap);
    return out;
  }
  // cls: 'major' | 'minor' | 'path' | 'rail' | any other (500 m)
  ribbonVisible(cls, chunkDist) { return this.e.vc_ribbon_visible(RIBBON_CLS.indexOf(cls), chunkDist) !== 0; }
  quadIndex(idx, q, a, g) {
    const o = this.e.vc_alloc(24);
    this.e.vc_quad_index(a, g, o);
    idx.set(new Uint32Array(this.e.memory.buffer, o, 6), q);
    this._free(o, 24);
    return q + 6;
  }
}

const RIBBON_CLS = ['major', 'minor', 'path', 'rail'];

// n (x, z) pairs through fn(ptrXZ, n, ptrOut) -> Float64Array(n)
function many(c, xz, fn) {
  const n = xz.length >> 1, p = c._in(xz), o = c.e.vc_alloc(8 * n);
  fn(p, n, o);
  const out = c._f64(o, n).slice();
  c._free(p, xz.byteLength); c._free(o, 8 * n);
  return out;
}

class Hf {
  constructor(c, u16, w, h, cell, zBase, zScale) {
    this.c = c;
    const p = c._in(u16);
    this.h = c.e.vc_hf_new(p, w, h, cell, zBase, zScale);
    c._free(p, u16.byteLength);
  }
  height(x, z) { return this.c.e.vc_hf_height(this.h, x, z); }
  heightMany(xz) { return many(this.c, xz, (p, n, o) => this.c.e.vc_hf_height_many(this.h, p, n, o)); }
  free() { this.c.e.vc_hf_free(this.h); this.h = 0; }
}

class Ground {
  constructor(c) {
    this.c = c;
    this.h = c.e.vc_gm_new();
    this._k = c.e.vc_alloc(12);
    this._t = c.e.vc_alloc(8);
  }
  // one GM03 node's bytes -> { k, tx, ty }; throws on bytes decodeNode refuses
  add(bytes) {
    const c = this.c, b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const p = c._in(b), r = c.e.vc_gm_add(this.h, p, b.length, this._k);
    c._free(p, b.length);
    if (r !== 0) throw new Error(c._err());
    const k = new Int32Array(c.e.memory.buffer, this._k, 3);
    return { k: k[0], tx: k[1], ty: k[2] };
  }
  // a GMIX1 index (Core.indexNode, e.g. a worker's instance) -> { k, tx, ty }:
  // the node add(its GM03 bytes) holds; throws on a buffer it refuses
  addIndex(bytes) {
    const c = this.c, b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const p = c._in(b), r = c.e.vc_gm_add_index(this.h, p, b.length, this._k);
    c._free(p, b.length);
    if (r !== 0) throw new Error(c._err());
    const k = new Int32Array(c.e.memory.buffer, this._k, 3);
    return { k: k[0], tx: k[1], ty: k[2] };
  }
  remove(k, tx, ty) { return this.c.e.vc_gm_remove(this.h, k, tx, ty) !== 0; }
  // GroundTree.skinY: the L0 skin, null where no node / over a hole
  skinY(x, z) {
    const y = this.c.e.vc_gm_skin(this.h, x, z);
    return Number.isFinite(y) ? y : null;
  }
  // NaN = none
  skinMany(xz) { return many(this.c, xz, (p, n, o) => this.c.e.vc_gm_skin_many(this.h, p, n, o)); }
  // lookup.js groundAt on node (k, tx, ty): { y (NaN none), t (-1 none) }
  nodeGround(k, tx, ty, lx, lz) {
    const y = this.c.e.vc_gm_node_ground(this.h, k, tx, ty, lx, lz, this._t);
    return { y, t: this.c._f64(this._t, 1)[0] };
  }
  // the node's mesh (codec meshOf), copies; null without the node
  mesh(k, tx, ty) {
    const c = this.c, n = c.e.vc_alloc(4), out = {};
    const T = [['x', Float64Array], ['z', Float64Array], ['y', Float64Array], ['idx', Uint32Array], ['lab', Uint8Array]];
    for (let f = 0; f < T.length; f++) {
      const p = c.e.vc_gm_mesh(this.h, k, tx, ty, f, n);
      if (!p) { c._free(n, 4); return null; }
      const len = new Uint32Array(c.e.memory.buffer, n, 1)[0];
      out[T[f][0]] = new T[f][1](c.e.memory.buffer, p, len).slice();
    }
    c._free(n, 4);
    return out;
  }
  free() { const e = this.c.e; e.vc_gm_free(this.h); e.vc_free(this._k, 12); e.vc_free(this._t, 8); this.h = 0; }
}

class Spans {
  constructor(c, h) {
    this.c = c; this.h = h;
    this._o = c.e.vc_alloc(16);
  }
  // Spans.resolve: null | { ground, ceiling (null: open) }
  resolve(x, z, feetY, surfaceY) {
    const f = this.c.e.vc_spans_resolve(this.h, x, z, feetY, surfaceY, this._o);
    if (!f) return null;
    const o = this.c._f64(this._o, 2);
    return { ground: o[0], ceiling: f === 2 ? o[1] : null };
  }
  // q: Float64Array of (x, z, feet, surface) rows -> { flag u8, g, c }
  resolveMany(q) {
    const c = this.c, n = q.length >> 2, p = c._in(q);
    const fl = c.e.vc_alloc(n), g = c.e.vc_alloc(8 * n), cc = c.e.vc_alloc(8 * n);
    c.e.vc_spans_resolve_many(this.h, p, n, fl, g, cc);
    const out = { flag: c._u8(fl, n).slice(), g: c._f64(g, n).slice(), c: c._f64(cc, n).slice() };
    c._free(p, q.byteLength); c._free(fl, n); c._free(g, 8 * n); c._free(cc, 8 * n);
    return out;
  }
  free() { this.c.e.vc_spans_free(this.h); this.c.e.vc_free(this._o, 16); this.h = 0; }
}

// the wasm has no imports
function instantiateSync(bytes) {
  return new Core(new WebAssembly.Instance(new WebAssembly.Module(bytes), {}));
}

// src: bytes, a WebAssembly.Module, or a Response / Promise of one
async function instantiate(src) {
  src = await src;
  if (src instanceof WebAssembly.Module) return new Core(await WebAssembly.instantiate(src, {}));
  if (typeof Response !== 'undefined' && src instanceof Response) {
    return new Core((await WebAssembly.instantiateStreaming(src, {})).instance);
  }
  return new Core((await WebAssembly.instantiate(src, {})).instance);
}

// a worker's raw instance exports (poolwork.js ctx.wasm) -> the glue's Core
function fromExports(exports) { return new Core({ exports }); }

module.exports = { instantiateSync, instantiate, fromExports };
